/**
 * IdeasResults.js
 * Renders the list of generated idea objects as rich cards.
 * Includes platform chips, format badges, and copy-to-clipboard functionality.
 */
import React, { useState } from 'react';
import {
    Box, Paper, Typography, Chip, IconButton, Tooltip,
    Avatar, alpha, Snackbar,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    CheckCircleOutline as CopiedIcon,
    TrendingUp as TrendingUpIcon,
    FormatQuote as QuoteIcon,
    RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { FORMAT_COLORS, ENGAGEMENT_COLORS, PLATFORM_COLORS } from './constants';

function IdeaCard({ idea, onCopy, copiedIdx, index }) {
    const isCopied = copiedIdx === index;
    const formatColor = FORMAT_COLORS[idea.format] || '#6366f1';
    const engagementColor = ENGAGEMENT_COLORS[idea.engagement] || '#9ca3af';

    return (
        <Box sx={{
            p: 2.5, borderRadius: 3,
            border: `1px solid ${alpha(formatColor, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(formatColor, 0.03)}, white)`,
            transition: 'all 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(formatColor, 0.1)}`,
                borderColor: alpha(formatColor, 0.3),
            },
            display: 'flex', gap: 2.5,
        }}>
            {/* Visual Indicator / Number */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <Avatar sx={{
                    bgcolor: alpha(formatColor, 0.1), color: formatColor,
                    width: 32, height: 32, fontSize: '0.85rem', fontWeight: 800,
                }}>
                    {index + 1}
                </Avatar>
                <Box sx={{ flex: 1, width: '2px', bgcolor: alpha(formatColor, 0.1), my: 1, borderRadius: 1 }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Header: Title + Badges */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.3, mb: 0.5 }}>
                            {idea.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            <Chip
                                label={idea.format} size="small"
                                sx={{
                                    bgcolor: alpha(formatColor, 0.1), color: formatColor,
                                    fontWeight: 700, height: 20, fontSize: '0.65rem',
                                }}
                            />
                            <Chip
                                icon={<TrendingUpIcon sx={{ fontSize: '0.8rem !important', color: `${engagementColor} !important` }} />}
                                label={`${idea.engagement} Engagement`} size="small"
                                sx={{
                                    bgcolor: alpha(engagementColor, 0.08), color: engagementColor,
                                    fontWeight: 700, height: 20, fontSize: '0.65rem',
                                }}
                            />
                        </Box>
                    </Box>
                    <Tooltip title={isCopied ? 'Copied!' : 'Copy idea'}>
                        <IconButton
                            size="small"
                            onClick={() => onCopy(idea, index)}
                            sx={{ color: isCopied ? '#10b981' : 'text.disabled' }}
                        >
                            {isCopied ? <CopiedIcon sx={{ fontSize: 20 }} /> : <CopyIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Hook */}
                <Box sx={{ mb: 2, display: 'flex', gap: 1.5 }}>
                    <QuoteIcon sx={{ color: alpha(formatColor, 0.2), fontSize: 28, mt: -0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontStyle: 'italic' }}>
                        {idea.hook}
                    </Typography>
                </Box>

                {/* Body */}
                <Box sx={{
                    bgcolor: 'rgba(248, 250, 252, 0.8)', p: 2, borderRadius: 2,
                    border: '1px solid #e2e8f0', mb: 2,
                }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {idea.body}
                    </Typography>
                    {idea.cta && (
                        <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700, color: formatColor }}>
                            CTA: {idea.cta}
                        </Typography>
                    )}
                </Box>

                {/* Footer: Platforms + Tags */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {idea.platforms.map(p => (
                            <Chip
                                key={p} label={p} size="small" variant="outlined"
                                sx={{
                                    height: 18, fontSize: '0.6rem', fontWeight: 600,
                                    borderColor: alpha(PLATFORM_COLORS[p] || '#6366f1', 0.2),
                                    color: PLATFORM_COLORS[p] || '#6366f1',
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {idea.tags.map(t => (
                            <Typography key={t} variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                                #{t}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default function IdeasResults({ ideaResult, card }) {
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [snackOpen, setSnackOpen] = useState(false);

    if (!ideaResult || !ideaResult.ideas) return null;

    const handleCopy = (idea, idx) => {
        const fullText = `${idea.title}\n\nHOOK: ${idea.hook}\n\n${idea.body}\n\nCTA: ${idea.cta}`;
        navigator.clipboard.writeText(fullText).then(() => {
            setCopiedIdx(idx);
            setSnackOpen(true);
            setTimeout(() => setCopiedIdx(null), 2000);
        });
    };

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <RocketIcon sx={{ color: '#10b981', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Generated Strategy for "{ideaResult.topic}"
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                We've generated {ideaResult.ideas.length} native content pieces optimized for {ideaResult.platform || 'multiple platforms'}.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {ideaResult.ideas.map((idea, i) => (
                    <IdeaCard
                        key={idea.id}
                        idea={idea}
                        index={i}
                        onCopy={handleCopy}
                        copiedIdx={copiedIdx}
                    />
                ))}
            </Box>

            <Snackbar
                open={snackOpen}
                autoHideDuration={2000}
                onClose={() => setSnackOpen(false)}
                message="Full content strategy copied!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Paper>
    );
}
